import { lazy, useCallback } from "react"
import type { FC, LazyExoticComponent, ReactElement } from "react"

import { type Params, useParams } from "react-router-dom"

import { Loadable, RootLoaderFallback } from "@/3514/components"
import {
    ECoachingSessionsApiMode,
    type TCoachingSessionsEntityProps,
    coachingSessionsEntityLoader
} from "@/3514/entities/coaching-sessions"
import {
    type TScreenBreakpoints,
    type TUserState,
    useCoachingSessionsAnimationsReset,
    useScreenBreakpoints,
    useUser
} from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import type { TEmptyCallback } from "@/shared/types/functions"

import classes from "./coach.group.styles"

const LazySessionsEntity: LazyExoticComponent<FC<TCoachingSessionsEntityProps>> = lazy(coachingSessionsEntityLoader)

const seenAnimationsPerParticipant: Map<number, boolean> = new Map()

const CoachGroupSessionsTab: FC = (): ReactElement => {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const { groupId: groupIdParam }: Readonly<Params> = useParams()

    useCoachingSessionsAnimationsReset(seenAnimationsPerParticipant, Number(groupIdParam))

    const { user }: TUserState<EUserRole.Coach> = useUser<EUserRole.Coach>()

    const handleEntityMount: TEmptyCallback = useCallback(
        (): void => void seenAnimationsPerParticipant.set(Number(groupIdParam), true),
        [groupIdParam]
    )

    return (
        <Loadable<TCoachingSessionsEntityProps>
            component={LazySessionsEntity}
            componentProps={{
                apiMode: ECoachingSessionsApiMode.Group,
                user,
                onMount: handleEntityMount,
                onQuerySuccess: undefined,
                groupId: Number(groupIdParam),
                sessionInfoViewerRole: user.role,
                wrapperClassName: classes.sessionsTab.container()
            }}
            loadingFallback={<RootLoaderFallback top={breakpoints.xs || breakpoints.sm ? "50%" : "25%"} />}
        />
    )
}

CoachGroupSessionsTab.displayName = "CoachGroupSessionsTab"

export default CoachGroupSessionsTab
